import { useContext } from "react";
import { AIManagerContext } from "../contexts/AIManagerContext";

/**
 * Custom hook to easily consume AIManagerContext
 *
 * @returns {Object} AIManager context values and methods
 *
 * @example
 * const {
 *   isProcessing,
 *   messages,
 *   sendAIRequest,
 *   clearMessages
 * } = useAIManager();
 *
 * // Send a request to generate text
 * sendAIRequest('generate_text', { prompt: 'Write a summary of...' });
 *
 * // Access the latest message
 * const latestMessage = messages[messages.length - 1];
 */
export const useAIManager = () => {
  const context = useContext(AIManagerContext);

  if (context === undefined) {
    throw new Error("useAIManager must be used within an AIManagerContextProvider");
  }

  return {
    isProcessing: context.isProcessing,
    messages: context.messages,
    completedMessages: context.completedMessages,
    sendAIRequest: context.sendAIRequest,
    clearMessages: context.clearMessages,
    totalClearMessages: context.totalClearMessages,

    // Helper methods
    getLatestMessage: () => context.messages.length > 0
      ? context.messages[context.messages.length - 1]
      : null,

    getMessagesByType: (requestType: string) =>
      context.messages.filter(msg => msg.request_type === requestType),

    getCompletedMessages: () =>
      context.completedMessages,

    hasErrorMessages: () =>
      context.messages.some(msg => msg.status === "error"),

    getErrorMessages: () =>
      context.messages.filter(msg => msg.status === "error")

  };
};
